import React from 'react';
import { AutomationStatus } from '../../types/tenant';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'SUCCESS':
      return '✅';
    case 'FAILURE':
      return '❌';
    case 'IN_PROGRESS':
      return '🔄';
    default:
      return '';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'SUCCESS':
      return '#28a745';
    case 'FAILURE':
      return '#dc3545';
    case 'IN_PROGRESS':
      return '#17a2b8';
    default:
      return '#6c757d';
  }
};

const AutomationItem = ({
  label,
  automation,
}: {
  label: string;
  automation?: AutomationStatus;
}) => {
  if (!automation?.status || automation.status === 'IDLE') return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',
        gap: '8px',
      }}
    >
      <span style={{ fontWeight: '500', minWidth: '120px' }}>{label}:</span>
      <span style={{ color: getStatusColor(automation.status) }}>
        {getStatusIcon(automation.status)}{' '}
        {automation.status === 'SUCCESS'
          ? 'Success'
          : automation.status === 'FAILURE'
          ? 'Failed'
          : 'In Progress'}
      </span>
      {automation.message && (
        <span style={{ fontSize: '11px', opacity: 0.8 }}>
          - {automation.message}
        </span>
      )}
    </div>
  );
};

export interface StatusDisplayProps {
  status: 'IDLE' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE';
  message: string;
  createdTenantName?: string;
  createdTenantId?: string;
  automationStarted?: boolean;
  detailedMessages?: readonly string[];
  showDetails: boolean;
  setShowDetails: (show: boolean) => void;
  clientAutomation?: AutomationStatus;
  dbAutomation?: AutomationStatus;
}

const StatusDisplay = React.memo<StatusDisplayProps>(
  ({
    status,
    message,
    createdTenantName,
    createdTenantId,
    automationStarted,
    detailedMessages,
    showDetails,
    setShowDetails,
    clientAutomation,
    dbAutomation,
  }) => {
    if (status === 'IDLE') return null;

    const statusStyles = {
      padding: '15px',
      margin: '10px 0',
      borderRadius: '8px',
      backgroundColor:
        status === 'SUCCESS'
          ? '#d4edda'
          : status === 'FAILURE'
          ? '#f8d7da'
          : '#d1ecf1',
      border:
        status === 'SUCCESS'
          ? '2px solid #28a745'
          : status === 'FAILURE'
          ? '2px solid #dc3545'
          : '2px solid #17a2b8',
      color:
        status === 'SUCCESS'
          ? '#155724'
          : status === 'FAILURE'
          ? '#721c24'
          : '#0c5460',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    };

    return (
      <div style={statusStyles}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {status === 'IN_PROGRESS' && '🔄 Creating Tenant...'}
            {status === 'SUCCESS' && '✅ Tenant Created Successfully!'}
            {status === 'FAILURE' && '❌ Tenant Creation Failed'}
          </div>

          {detailedMessages && detailedMessages.length > 1 && (
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              style={{
                background: 'none',
                border: '1px solid currentColor',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          )}
        </div>

        <div style={{ marginBottom: '8px', fontSize: '14px' }}>{message}</div>

        {createdTenantName && (
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '8px' }}>
            Tenant: <strong>{createdTenantName}</strong>
            {createdTenantId && <span> (ID: {createdTenantId.slice(-8)})</span>}
          </div>
        )}

        {status === 'IN_PROGRESS' && (
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '8px' }}>
            {automationStarted
              ? 'Automation in progress... This may take a few minutes.'
              : 'Waiting for automation to start...'}
          </div>
        )}

        {/* Individual Automation Status */}
        {((clientAutomation?.status && clientAutomation.status !== 'IDLE') ||
          (dbAutomation?.status && dbAutomation.status !== 'IDLE')) && (
          <div
            style={{
              marginTop: '10px',
              padding: '8px',
              backgroundColor: 'rgba(255,255,255,0.4)',
              borderRadius: '4px',
              fontSize: '12px',
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
              Automation Status:
            </div>
            <AutomationItem
              label="Client Automation"
              automation={clientAutomation}
            />
            <AutomationItem label="DB Automation" automation={dbAutomation} />
          </div>
        )}

        {showDetails && detailedMessages && detailedMessages.length > 0 && (
          <div
            style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderRadius: '4px',
              fontSize: '12px',
              maxHeight: '150px',
              overflowY: 'auto',
              fontFamily: 'monospace',
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
              Detailed Progress:
            </div>
            {detailedMessages.map((msg, index) => (
              <div key={index} style={{ margin: '2px 0' }}>
                {msg}
              </div>
            ))}
          </div>
        )}

        {status === 'SUCCESS' && (
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px' }}>
            This modal will close automatically in a few seconds...
          </div>
        )}
      </div>
    );
  }
);

StatusDisplay.displayName = 'StatusDisplay';

export default StatusDisplay;
